import React, { useEffect, useMemo, useState } from 'react';
import { MainLayout } from '../../../../components/Layout';
import {
  LoadingPanel,
  RevoltButton,
  DropdownSelect,
} from '../../../../components';
import { AdminPageHeader } from '../../components';
import { AdminInferencesManager } from './managers/admin-inferences-manager';
import { useManager } from '../../../../core';
import { InferencePlotChart } from './components/InferencePlotChart';
import { InferenceStackedBarChart } from './components/InferenceStackedBarChart';
import './AdminInferencesSection.css';

function toUtcDateOnlyString(date) {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const MS_DAY = 24 * 60 * 60 * 1000;

/** Aligns custom date fields with server rolling windows (server uses instant `now` at query time). */
function getRangeFromPreset(preset, referenceNow = new Date()) {
  const now =
    referenceNow instanceof Date && !Number.isNaN(referenceNow.getTime())
      ? referenceNow
      : new Date();

  if (preset === 'last_day') {
    const start = new Date(now.getTime() - MS_DAY);
    return { customStart: toUtcDateOnlyString(start), customEnd: toUtcDateOnlyString(now) };
  }

  if (preset === 'last_week') {
    const start = new Date(now.getTime() - 7 * MS_DAY);
    return { customStart: toUtcDateOnlyString(start), customEnd: toUtcDateOnlyString(now) };
  }

  if (preset === 'last_month') {
    const start = new Date(now.getTime() - 30 * MS_DAY);
    return { customStart: toUtcDateOnlyString(start), customEnd: toUtcDateOnlyString(now) };
  }

  if (preset === 'month_to_date') {
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    return { customStart: toUtcDateOnlyString(start), customEnd: toUtcDateOnlyString(now) };
  }

  if (preset === 'year_to_date') {
    const start = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
    return { customStart: toUtcDateOnlyString(start), customEnd: toUtcDateOnlyString(now) };
  }

  if (preset === 'revolt_start') {
    const start = new Date(Date.UTC(2026, 1, 16));
    return { customStart: toUtcDateOnlyString(start), customEnd: toUtcDateOnlyString(now) };
  }

  const endStr = toUtcDateOnlyString(now);
  return { customStart: endStr, customEnd: endStr };
}

function StatCard({ title, value, subtitle, icon, color = '#8b0000' }) {
  const displayValue =
    typeof value === 'number'
      ? value.toLocaleString()
      : typeof value === 'string' && value.trim().length > 0
        ? value
        : '—';

  return (
    <div className="admin-stat-card">
      <div className="admin-stat-icon" style={{ color }}>
        <i className={`fa-solid fa-${icon}`}></i>
      </div>
      <div className="admin-stat-content">
        <h3 className="admin-stat-value">{displayValue}</h3>
        <h4 className="admin-stat-title">{title}</h4>
        {subtitle && <p className="admin-stat-subtitle">{subtitle}</p>}
      </div>
    </div>
  );
}

function formatIsoDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function formatCost(value) {
  if (typeof value !== 'number') return '—';
  return `$${value.toFixed(6)}`;
}

function formatMetricValue(value, metric) {
  if (metric === 'cost') {
    return formatCost(Number(value || 0) || 0);
  }

  return (Number(value || 0) || 0).toLocaleString();
}

function buildDefaultQuery() {
  const defaultRange = getRangeFromPreset('last_day', new Date());

  return {
    rangePreset: 'last_day',
    customStart: defaultRange.customStart,
    customEnd: defaultRange.customEnd,
    granularity: 'hour',
    displayMode: 'bar',
    limit: 1000,
    mode: 'all',
    instance: '',
    phase: '',
    status: '',
    modelId: '',
    provider: '',
    userId: '',
    appId: '',
    membershipId: '',
  };
}

export function AdminInferencesSection() {
  const defaultQuery = useMemo(() => buildDefaultQuery(), []);
  const { manager, isInitialized } = useManager(AdminInferencesManager, defaultQuery);

  const [queryState, setQueryState] = useState(defaultQuery);
  const [visualizationState, setVisualizationState] = useState({
    metric: 'total_tokens',
    breakdownField: 'modelId',
    breakdownScope: 'all',
    breakdownValue: '',
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [granularityNotice, setGranularityNotice] = useState('');

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await manager.refreshRecords(queryState);
    } finally {
      setIsRefreshing(false);
    }
  };

  const applyQuery = async (nextQuery) => {
    setIsApplying(true);
    try {
      await manager.refreshRecords(nextQuery);
    } finally {
      setIsApplying(false);
    }
  };

  const handleRangePresetChange = async (nextPreset) => {
    const now = new Date();
    setGranularityNotice('');

    if (nextPreset === 'custom') {
      setQueryState((prev) => ({
        ...prev,
        rangePreset: 'custom',
      }));
      return;
    }

    const nextRange = getRangeFromPreset(nextPreset, now);
    const nextQuery = {
      ...queryState,
      rangePreset: nextPreset,
      customStart: nextRange.customStart,
      customEnd: nextRange.customEnd,
    };

    setQueryState(nextQuery);
    await applyQuery(nextQuery);
  };

  const handleApplyFilters = async () => {
    setGranularityNotice('');
    await applyQuery(queryState);
  };

  const handleResetFilters = async () => {
    const nextQuery = buildDefaultQuery();
    setGranularityNotice('');
    setQueryState(nextQuery);
    await applyQuery(nextQuery);
  };

  const handleGranularitySelection = (nextGranularity) => {
    setGranularityNotice('');
    setQueryState((prev) => ({ ...prev, granularity: nextGranularity }));
  };

  const summary = manager.getSummary(queryState);
  const meta = manager.getMeta();
  const range = manager.getRange();
  const lastError = manager.getLastError();
  const visualizationOptions = manager.getVisualizationOptions(visualizationState.breakdownField);

  const loadedCount = Number(meta?.count || 0) || 0;
  const effectiveLimit = Number(meta?.limit || queryState?.limit || 1000) || 1000;
  const didHitResultLimit = loadedCount >= effectiveLimit && (meta?.total || 0) > loadedCount;

  useEffect(() => {
    if (visualizationState.breakdownField === 'none') {
      if (visualizationState.breakdownScope !== 'all' || visualizationState.breakdownValue) {
        setVisualizationState((prev) => ({
          ...prev,
          breakdownScope: 'all',
          breakdownValue: '',
        }));
      }
      return;
    }

    const availableValues = visualizationOptions.breakdownValueOptions.map((option) => option.value);
    if (
      visualizationState.breakdownScope === 'single' &&
      visualizationState.breakdownValue &&
      !availableValues.includes(visualizationState.breakdownValue)
    ) {
      setVisualizationState((prev) => ({
        ...prev,
        breakdownValue: availableValues[0] || '',
      }));
    }
  }, [visualizationOptions.breakdownValueOptions, visualizationState]);

  const visualizationData = manager.getVisualizationData({
    granularity: queryState.granularity,
    metric: visualizationState.metric,
    breakdownField: visualizationState.breakdownField,
    breakdownScope: visualizationState.breakdownScope,
    breakdownValue: visualizationState.breakdownValue,
    chartType: queryState.displayMode,
  });

  useEffect(() => {
    if (
      visualizationData.wasEscalated &&
      visualizationData.effectiveGranularity &&
      visualizationData.effectiveGranularity !== queryState.granularity
    ) {
      setGranularityNotice(visualizationData.message || '');
      setQueryState((prev) =>
        prev.granularity === visualizationData.effectiveGranularity
          ? prev
          : { ...prev, granularity: visualizationData.effectiveGranularity }
      );
      return;
    }

    if (!visualizationData.canRender && visualizationData.message) {
      setGranularityNotice(visualizationData.message);
    }
  }, [
    queryState.granularity,
    visualizationData.canRender,
    visualizationData.effectiveGranularity,
    visualizationData.message,
    visualizationData.wasEscalated,
  ]);

  const activeBreakdownValueCount = visualizationData.seriesKeys.length;
  const chartModeLabel = queryState.displayMode === 'bar' ? 'Stacked bar chart' : 'Plot chart';
  const displayedGranularity =
    visualizationData.effectiveGranularity || queryState.granularity;
  const breakdownLabel =
    visualizationState.breakdownField === 'none'
      ? 'No breakdown'
      : visualizationState.breakdownScope === 'single' && visualizationState.breakdownValue
        ? `${visualizationState.breakdownField}: ${visualizationState.breakdownValue}`
        : `${visualizationState.breakdownField} (${activeBreakdownValueCount.toLocaleString()} values)`;

  if (!isInitialized) {
    return (
      <MainLayout contentClassName="main-content--admin">
        <div className="loading-container">
          <LoadingPanel
            title="Loading Inferences"
            subtitle="Loading inference records and admin controls..."
            showAppName={false}
          />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout contentClassName="main-content--admin">
      <AdminPageHeader />

      <div className="admin-inferences-container">
        <div className="admin-section-content">
          <div className="admin-section-header">
            <h2>Inferences</h2>
            <p className="admin-section-subtitle">
              Query inference records by started time and additive filters. This first pass is
              focused on scalable structure and data access.
            </p>
            <RevoltButton
              className="admin-refresh-button"
              onClick={handleRefresh}
              disabled={isRefreshing || isApplying}
            >
              <i className={`fa-solid fa-rotate-right ${isRefreshing ? 'fa-spin' : ''}`}></i>
              Refresh
            </RevoltButton>
          </div>

          {lastError && (
            <div className="admin-inferences-error-banner">
              <i className="fa-solid fa-triangle-exclamation"></i>
              <span>{lastError}</span>
            </div>
          )}

          <div className="admin-stats-grid">
            <StatCard
              title="Matching Records"
              value={summary.totalMatchingRecords}
              subtitle="Total records matching the current query"
              icon="wave-square"
              color="#8b0000"
            />
            <StatCard
              title="Loaded Records"
              value={summary.loadedRecords}
              subtitle={`Current page size: ${(summary.queryLimit || 0).toLocaleString()}`}
              icon="table-list"
              color="#3b82f6"
            />
            <StatCard
              title="Active Filters"
              value={summary.activeFilterCount}
              subtitle="Current additive filters applied"
              icon="filter"
              color="#10b981"
            />
            <StatCard
              title="Range Field"
              value={summary.rangeField}
              subtitle="Default time anchor for querying"
              icon="clock"
              color="#f59e0b"
            />
          </div>

          <div className="admin-inferences-controls-card stripe-chart-section">
            <div className="admin-inferences-controls-header">
              <div className="admin-inferences-controls-copy">
                <h4>Controls</h4>
                <p>
                  Configure result limit, mode, and additive filters. Chart display, time range, and
                  granularity are set in the visualization area below.
                </p>
              </div>
              <div className="admin-inferences-action-group">
                <RevoltButton
                  className="admin-inferences-apply-button"
                  onClick={handleApplyFilters}
                  disabled={isApplying || isRefreshing}
                >
                  {isApplying ? 'Applying...' : 'Apply Filters'}
                </RevoltButton>
                <RevoltButton
                  className="admin-inferences-reset-button"
                  onClick={handleResetFilters}
                  disabled={isApplying || isRefreshing}
                >
                  Reset
                </RevoltButton>
              </div>
            </div>

            <div className="admin-inferences-controls-grid">
              <div className="admin-inferences-control-group">
                <label className="admin-inferences-control-label">Result Limit</label>
                <DropdownSelect
                  id="admin-inferences-limit-select"
                  value={String(queryState.limit)}
                  options={[
                    { value: '1000', label: '1,000' },
                    { value: '5000', label: '5,000' },
                    { value: '20000', label: '20,000' },
                  ]}
                  onValueChange={(value) =>
                    setQueryState((prev) => ({
                      ...prev,
                      limit: Number(value) || 1000,
                    }))
                  }
                  disabled={isApplying || isRefreshing}
                  className="admin-inferences-range-select"
                />
              </div>

              <div className="admin-inferences-control-group">
                <label className="admin-inferences-control-label">Mode</label>
                <DropdownSelect
                  id="admin-inferences-mode-select"
                  value={queryState.mode}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'cloud', label: 'Cloud' },
                    { value: 'local', label: 'Local' },
                  ]}
                  onValueChange={(value) => setQueryState((prev) => ({ ...prev, mode: value }))}
                  disabled={isApplying || isRefreshing}
                  className="admin-inferences-range-select"
                />
              </div>

              <div className="admin-inferences-control-group">
                <label className="admin-inferences-control-label">Instance</label>
                <input
                  type="text"
                  className="admin-inferences-input"
                  placeholder="workspace, app"
                  value={queryState.instance}
                  onChange={(e) => setQueryState((prev) => ({ ...prev, instance: e.target.value }))}
                  disabled={isApplying || isRefreshing}
                />
              </div>

              <div className="admin-inferences-control-group">
                <label className="admin-inferences-control-label">Phase</label>
                <input
                  type="text"
                  className="admin-inferences-input"
                  placeholder="streaming, finished"
                  value={queryState.phase}
                  onChange={(e) => setQueryState((prev) => ({ ...prev, phase: e.target.value }))}
                  disabled={isApplying || isRefreshing}
                />
              </div>

              <div className="admin-inferences-control-group">
                <label className="admin-inferences-control-label">Status</label>
                <input
                  type="text"
                  className="admin-inferences-input"
                  placeholder="completed, cancelled"
                  value={queryState.status}
                  onChange={(e) => setQueryState((prev) => ({ ...prev, status: e.target.value }))}
                  disabled={isApplying || isRefreshing}
                />
              </div>

              <div className="admin-inferences-control-group">
                <label className="admin-inferences-control-label">Model ID</label>
                <input
                  type="text"
                  className="admin-inferences-input"
                  placeholder="openai/gpt-5.4-nano"
                  value={queryState.modelId}
                  onChange={(e) => setQueryState((prev) => ({ ...prev, modelId: e.target.value }))}
                  disabled={isApplying || isRefreshing}
                />
              </div>

              <div className="admin-inferences-control-group">
                <label className="admin-inferences-control-label">Provider</label>
                <input
                  type="text"
                  className="admin-inferences-input"
                  placeholder="openrouter"
                  value={queryState.provider}
                  onChange={(e) => setQueryState((prev) => ({ ...prev, provider: e.target.value }))}
                  disabled={isApplying || isRefreshing}
                />
              </div>

              <div className="admin-inferences-control-group">
                <label className="admin-inferences-control-label">User ID</label>
                <input
                  type="text"
                  className="admin-inferences-input"
                  placeholder="6914fc..."
                  value={queryState.userId}
                  onChange={(e) => setQueryState((prev) => ({ ...prev, userId: e.target.value }))}
                  disabled={isApplying || isRefreshing}
                />
              </div>

              <div className="admin-inferences-control-group">
                <label className="admin-inferences-control-label">App ID</label>
                <input
                  type="text"
                  className="admin-inferences-input"
                  placeholder="app_123"
                  value={queryState.appId}
                  onChange={(e) => setQueryState((prev) => ({ ...prev, appId: e.target.value }))}
                  disabled={isApplying || isRefreshing}
                />
              </div>

              <div className="admin-inferences-control-group">
                <label className="admin-inferences-control-label">Membership ID</label>
                <input
                  type="text"
                  className="admin-inferences-input"
                  placeholder="mem_123"
                  value={queryState.membershipId}
                  onChange={(e) => setQueryState((prev) => ({ ...prev, membershipId: e.target.value }))}
                  disabled={isApplying || isRefreshing}
                />
              </div>
            </div>

            <div className="admin-inferences-result-limit-row">
              <span className="admin-inferences-result-limit-text">
                Loaded results: <strong>{loadedCount.toLocaleString()}</strong> / {effectiveLimit.toLocaleString()}
              </span>
              {didHitResultLimit && (
                <span className="admin-inferences-limit-hit-badge">Result limit hit</span>
              )}
            </div>
          </div>

          <div className="admin-inferences-visualization-card stripe-chart-section">
            <div className="admin-inferences-visualization-header">
              <div>
                <h4>Visualization Area</h4>
                <p>
                  Visualize the current loaded records as a multi-line plot or stacked bar chart.
                  Set the time range, granularity, and display options here; metric and breakdown use
                  the loaded result set.
                </p>
              </div>
            </div>

            <div className="admin-inferences-visualization-controls-grid">
              <div className="admin-inferences-visualization-controls-row">
                <div className="admin-inferences-control-group">
                  <label className="admin-inferences-control-label">Display Mode</label>
                  <div className="stripe-chart-toggle">
                    <button
                      type="button"
                      className={`stripe-toggle-btn ${queryState.displayMode === 'plot' ? 'active' : ''}`}
                      onClick={() => setQueryState((prev) => ({ ...prev, displayMode: 'plot' }))}
                      disabled={isApplying || isRefreshing}
                    >
                      Plot
                    </button>
                    <button
                      type="button"
                      className={`stripe-toggle-btn ${queryState.displayMode === 'bar' ? 'active' : ''}`}
                      onClick={() => setQueryState((prev) => ({ ...prev, displayMode: 'bar' }))}
                      disabled={isApplying || isRefreshing}
                    >
                      Bar
                    </button>
                  </div>
                </div>

                <div className="admin-inferences-control-group">
                  <label className="admin-inferences-control-label">Granularity</label>
                  <div className="stripe-chart-toggle">
                    <button
                      type="button"
                      className={`stripe-toggle-btn ${queryState.granularity === 'minute' ? 'active' : ''}`}
                      onClick={() => handleGranularitySelection('minute')}
                      disabled={isApplying || isRefreshing}
                    >
                      Minute
                    </button>
                    <button
                      type="button"
                      className={`stripe-toggle-btn ${queryState.granularity === 'hour' ? 'active' : ''}`}
                      onClick={() => handleGranularitySelection('hour')}
                      disabled={isApplying || isRefreshing}
                    >
                      Hour
                    </button>
                    <button
                      type="button"
                      className={`stripe-toggle-btn ${queryState.granularity === 'day' ? 'active' : ''}`}
                      onClick={() => handleGranularitySelection('day')}
                      disabled={isApplying || isRefreshing}
                    >
                      Day
                    </button>
                    <button
                      type="button"
                      className={`stripe-toggle-btn ${queryState.granularity === 'week' ? 'active' : ''}`}
                      onClick={() => handleGranularitySelection('week')}
                      disabled={isApplying || isRefreshing}
                    >
                      Week
                    </button>
                    <button
                      type="button"
                      className={`stripe-toggle-btn ${queryState.granularity === 'month' ? 'active' : ''}`}
                      onClick={() => handleGranularitySelection('month')}
                      disabled={isApplying || isRefreshing}
                    >
                      Month
                    </button>
                  </div>
                </div>

                <div className="admin-inferences-control-group">
                  <label className="admin-inferences-control-label">Range</label>
                  <DropdownSelect
                    id="admin-inferences-range-select"
                    value={queryState.rangePreset}
                    options={[
                      { value: 'last_day', label: 'Last day' },
                      { value: 'last_week', label: 'Last week' },
                      { value: 'last_month', label: 'Last month' },
                      { value: 'month_to_date', label: 'Month to date' },
                      { value: 'year_to_date', label: 'Year to date' },
                      { value: 'revolt_start', label: 'Revolt Start' },
                      { value: 'custom', label: 'Custom' },
                    ]}
                    onValueChange={handleRangePresetChange}
                    disabled={isApplying || isRefreshing}
                    className="admin-inferences-range-select"
                  />
                </div>
              </div>

              {queryState.rangePreset === 'custom' && (
                <div className="admin-inferences-visualization-controls-row">
                  <div className="admin-inferences-control-group">
                    <label className="admin-inferences-control-label">Custom Start</label>
                    <input
                      type="date"
                      className="admin-inferences-input"
                      value={queryState.customStart}
                      onChange={(e) => setQueryState((prev) => ({ ...prev, customStart: e.target.value }))}
                      disabled={isApplying || isRefreshing}
                    />
                  </div>
                  <div className="admin-inferences-control-group">
                    <label className="admin-inferences-control-label">Custom End</label>
                    <input
                      type="date"
                      className="admin-inferences-input"
                      value={queryState.customEnd}
                      onChange={(e) => setQueryState((prev) => ({ ...prev, customEnd: e.target.value }))}
                      disabled={isApplying || isRefreshing}
                    />
                  </div>
                </div>
              )}

              <div className="admin-inferences-visualization-controls-row">
                <div className="admin-inferences-control-group">
                  <label className="admin-inferences-control-label">Metric</label>
                  <DropdownSelect
                    id="admin-inferences-visualization-metric"
                    value={visualizationState.metric}
                    options={visualizationOptions.metricOptions}
                    onValueChange={(value) => setVisualizationState((prev) => ({ ...prev, metric: value }))}
                    disabled={isApplying || isRefreshing}
                    className="admin-inferences-range-select"
                  />
                </div>

                <div className="admin-inferences-control-group">
                  <label className="admin-inferences-control-label">Breakdown</label>
                  <DropdownSelect
                    id="admin-inferences-visualization-breakdown"
                    value={visualizationState.breakdownField}
                    options={visualizationOptions.breakdownFieldOptions}
                    onValueChange={(value) =>
                      setVisualizationState((prev) => ({
                        ...prev,
                        breakdownField: value,
                        breakdownScope: 'all',
                        breakdownValue: '',
                      }))}
                    disabled={isApplying || isRefreshing}
                    className="admin-inferences-range-select"
                  />
                </div>

                {visualizationState.breakdownField !== 'none' && (
                  <div className="admin-inferences-control-group">
                    <label className="admin-inferences-control-label">Series Scope</label>
                    <DropdownSelect
                      id="admin-inferences-visualization-breakdown-scope"
                      value={visualizationState.breakdownScope}
                      options={[
                        { value: 'all', label: 'All values' },
                        { value: 'single', label: 'Single value' },
                      ]}
                      onValueChange={(value) =>
                        setVisualizationState((prev) => ({
                          ...prev,
                          breakdownScope: value,
                          breakdownValue:
                            value === 'single'
                              ? prev.breakdownValue ||
                                  visualizationOptions.breakdownValueOptions[0]?.value ||
                                  ''
                              : '',
                        }))}
                      disabled={isApplying || isRefreshing}
                      className="admin-inferences-range-select"
                    />
                  </div>
                )}

                {visualizationState.breakdownField !== 'none' &&
                  visualizationState.breakdownScope === 'single' && (
                    <div className="admin-inferences-control-group">
                      <label className="admin-inferences-control-label">
                        {visualizationState.breakdownField === 'modelId'
                          ? 'Model ID'
                          : 'Instance Value'}
                      </label>
                      <DropdownSelect
                        id="admin-inferences-visualization-breakdown-value"
                        value={
                          visualizationState.breakdownValue ||
                          visualizationOptions.breakdownValueOptions[0]?.value ||
                          ''
                        }
                        options={
                          visualizationOptions.breakdownValueOptions.length > 0
                            ? visualizationOptions.breakdownValueOptions
                            : [{ value: '', label: 'No values available' }]
                        }
                        onValueChange={(value) =>
                          setVisualizationState((prev) => ({ ...prev, breakdownValue: value }))
                        }
                        disabled={
                          isApplying ||
                          isRefreshing ||
                          visualizationOptions.breakdownValueOptions.length === 0
                        }
                        className="admin-inferences-range-select"
                      />
                    </div>
                  )}
              </div>
            </div>

            {granularityNotice && (
              <div className="admin-inferences-granularity-note">
                <i className="fa-solid fa-circle-info"></i>
                <span>{granularityNotice}</span>
              </div>
            )}

            <div className="admin-inferences-visualization-summary-card">
              <div className="admin-inferences-visualization-icon">
                <i className={`fa-solid ${queryState.displayMode === 'bar' ? 'fa-chart-column' : 'fa-chart-line'}`}></i>
              </div>
              <div className="admin-inferences-visualization-copy">
                <h5>{chartModeLabel}</h5>
                <p>
                  Showing <strong>{manager.getMetricLabel(visualizationState.metric)}</strong> by{' '}
                  <strong>{displayedGranularity}</strong> using the currently loaded records.
                  Breakdown: <strong>{breakdownLabel}</strong>. Theoretical buckets:{' '}
                  <strong>{(visualizationData.theoreticalBucketCount || 0).toLocaleString()}</strong> /
                  <strong> {((visualizationData.pointLimit || 0) || 0).toLocaleString()}</strong>.
                </p>
              </div>
            </div>

            <div className="admin-inferences-chart-surface">
              {!visualizationData.canRender ? (
                <div className="admin-inferences-chart-placeholder">
                  <div className="admin-inferences-chart-placeholder-icon">
                    <i className="fa-solid fa-chart-simple"></i>
                  </div>
                  <div className="admin-inferences-chart-placeholder-copy">
                    <h5>Chart cannot render for this range</h5>
                    <p>{visualizationData.message || 'The selected range is too dense for this chart.'}</p>
                  </div>
                </div>
              ) : queryState.displayMode === 'bar' ? (
                <InferenceStackedBarChart
                  chartData={visualizationData.chartData}
                  seriesKeys={visualizationData.seriesKeys}
                  metric={visualizationState.metric}
                  metricLabel={visualizationData.metricLabel}
                  getSeriesColor={(key) => manager.getSeriesColor(key)}
                  isLoading={isApplying || isRefreshing}
                  xDomainStartMs={visualizationData.xDomainStartMs}
                  xDomainEndExclusiveMs={visualizationData.xDomainEndExclusiveMs}
                  axisStartLabel={visualizationData.axisStartLabel}
                  axisEndLabel={visualizationData.axisEndLabel}
                  granularity={displayedGranularity}
                />
              ) : (
                <InferencePlotChart
                  chartData={visualizationData.chartData}
                  seriesKeys={visualizationData.seriesKeys}
                  metric={visualizationState.metric}
                  metricLabel={visualizationData.metricLabel}
                  getSeriesColor={(key) => manager.getSeriesColor(key)}
                  isLoading={isApplying || isRefreshing}
                  xDomainStartMs={visualizationData.xDomainStartMs}
                  xDomainEndExclusiveMs={visualizationData.xDomainEndExclusiveMs}
                  axisStartLabel={visualizationData.axisStartLabel}
                  axisEndLabel={visualizationData.axisEndLabel}
                  granularity={displayedGranularity}
                />
              )}
            </div>

            <div className="admin-inferences-range-summary">
              <span>
                <strong>Resolved start:</strong> {formatIsoDate(range?.start)}
              </span>
              <span>
                <strong>Resolved end:</strong> {formatIsoDate(range?.endExclusive)}
              </span>
              <span>
                <strong>Total matches:</strong> {(meta.total || 0).toLocaleString()}
              </span>
              <span>
                <strong>Chart metric:</strong> {manager.getMetricLabel(visualizationState.metric)}
              </span>
              <span>
                <strong>Current plotted total:</strong>{' '}
                {formatMetricValue(
                  visualizationData.chartData.reduce((sum, bucket) => sum + (bucket?.total || 0), 0),
                  visualizationState.metric
                )}
              </span>
            </div>
          </div>

        </div>
      </div>
    </MainLayout>
  );
}
